/**
 * Builds the POST /orders payload for Signage Create Order.
 * Extracted from SignageCreateOrder — same logic; state is passed via deps.
 */
import { round } from "lodash";
import type { MutableRefObject } from "react";
import { ShippingCost } from "types";
import { readResolveTaxable, resolveCatalogProductLineTaxable, readResolveRequired } from "orderResolve/utils/readResolveTaxable";

const SALES_ZERO_OID = "000000000000000000000000";
const SALES_ZERO_DATE_PREFIX = "0001-01-01";

function isSalesZeroObjectId(value: unknown): boolean {
  const s = String(value ?? "").trim();
  return !s || s === SALES_ZERO_OID;
}

function isSalesZeroDate(value: unknown): boolean {
  const s = String(value ?? "").trim();
  return !s || s.startsWith(SALES_ZERO_DATE_PREFIX);
}

function omitZeroObjectIdField(obj: Record<string, unknown>, key: string): void {
  if (isSalesZeroObjectId(obj[key])) delete obj[key];
}

function sanitizeActorStamp(
  actor: unknown
): { id?: string; name?: string } | undefined {
  if (!actor || typeof actor !== "object") return undefined;
  const raw = actor as { id?: unknown; name?: unknown };
  const id = isSalesZeroObjectId(raw.id) ? "" : String(raw.id ?? "").trim();
  const name = String(raw.name ?? "").trim();
  if (!id && !name) return undefined;
  return {
    ...(id ? { id } : {}),
    ...(name ? { name } : {}),
  };
}

export function resolveSalesOrderAddressId(
  address?: { _id?: string; address_details?: { _id?: string } } | null
): string {
  if (!address) return "";
  const rootId = String(address._id ?? "").trim();
  if (rootId && rootId !== SALES_ZERO_OID) return rootId;
  const detailId = String(address.address_details?._id ?? "").trim();
  if (detailId && detailId !== SALES_ZERO_OID) return detailId;
  return "";
}

export function findSalesOrderAddressById(
  addresses: Array<{ _id?: string; address_details?: { _id?: string } }> | null | undefined,
  id?: string | null
): any | null {
  const needle = String(id ?? "").trim();
  if (!needle || needle === SALES_ZERO_OID) return null;
  return (
    (addresses || []).find(
      (a) =>
        resolveSalesOrderAddressId(a) === needle ||
        String(a?._id ?? "") === needle ||
        String(a?.address_details?._id ?? "") === needle
    ) || null
  );
}

/** Drop Go zero ObjectIDs / zero dates so POST /orders does not send placeholders. */
export function sanitizeSalesOrderAddressForPayload(address: unknown): Record<string, unknown> | null {
  if (!address || typeof address !== "object") return null;
  const src = address as Record<string, unknown>;
  const detailsSrc =
    src.address_details && typeof src.address_details === "object"
      ? ({ ...(src.address_details as Record<string, unknown>) } as Record<string, unknown>)
      : null;

  const line1 = String(detailsSrc?.addressLine1 ?? "").trim();
  const rootId = String(src._id ?? "").trim();
  const detailId = String(detailsSrc?._id ?? "").trim();
  const hasUsableId =
    (!isSalesZeroObjectId(rootId) && !!rootId) ||
    (!isSalesZeroObjectId(detailId) && !!detailId);
  if (!hasUsableId && !line1) return null;

  const out: Record<string, unknown> = { ...src };
  delete out.value;

  omitZeroObjectIdField(out, "_id");
  omitZeroObjectIdField(out, "user_id");
  omitZeroObjectIdField(out, "owner_node_id");

  if (!String(out.user_type ?? "").trim()) delete out.user_type;
  if (!String(out.owner_node_type ?? "").trim()) delete out.owner_node_type;

  if (isSalesZeroDate(out.created_at)) delete out.created_at;
  if (isSalesZeroDate(out.updated_at)) delete out.updated_at;

  const createdBy = sanitizeActorStamp(out.created_by);
  if (createdBy) out.created_by = createdBy;
  else delete out.created_by;

  const updatedBy = sanitizeActorStamp(out.updated_by);
  if (updatedBy) out.updated_by = updatedBy;
  else delete out.updated_by;

  if (detailsSrc) {
    omitZeroObjectIdField(detailsSrc, "_id");
    out.address_details = detailsSrc;
  }

  return out;
}

function firstAddressCandidate(...candidates: unknown[]): unknown {
  for (const c of candidates) {
    if (c && typeof c === "object") return c;
  }
  return null;
}

type ShipPayload = {
  shipping_option?: any[] | null;
  isUps?: boolean;
  isManual?: boolean;
  selected_ids?: unknown;
  ship_to?: unknown;
  packages?: unknown[];
};

type FeePayload = {
  specialfee?: any[] | null;
  selected_fee_ids?: string[];
  total_amount?: number;
  total_tax_amount?: number;
  total_tax_percentage?: number;
};

/** Matches `useState` for manual shipping method in SignageCreateOrder. */
export type SelectedShippingMethodState = {
  value: string;
  label: string;
  cost: number;
} | null;

export type BuildSalesOrderPayloadDeps = {
  token: string | undefined | null;
  ensureDraftUniqueId: (token: string, cartId: string | null) => Promise<string | null>;
  shippingCosts: ShippingCost[];
  /** Loose shapes from page state (same as pre-extraction inline builder). */
  shipPayloadState: any;
  feePayloadState: any;
  selectedBillingAddress: any;
  selectedShippingAddress: any;
  productTotals: { productTax?: number; subTotal?: number; discount?: number };
  shippingTaxAmount: any;
  specialFeeTaxAmount: any;
  productTaxMap: any;
  perItemCouponMap: any;
  couponDiscounts: any[] | null | undefined;
  selectedBrokerageOption: any;
  selectedOfficeAgent: any;
  selectedAgent: any;
  selectedOffice: any;
  selectedTeamMemberOption: any;
  offices: any[] | null | undefined;
  selectedOfficeBillingAddress: any;
  selectedOfficeShippingAddress: any;
  addressObjects?: any[] | null;
  projectPrefillAddresses?: { billing?: any | null; shipping?: any | null } | null;
  teamMemberFromContinueRef: MutableRefObject<{ value: string; label: string } | null>;
  customproducts: any[] | null | undefined;
  selectedProducts: any[] | null | undefined;
  ProductObjs: any[] | null | undefined;
  linkedProjectId: string | null;
  projectIdFromQuery: string;
  linkedProjectNumber: string | null | undefined;
  isSignageTeammember: boolean;
  parentSignageId: string | null | undefined;
  parentSignageName: string | null | undefined;
  userId: string | null | undefined;
  userName: string | null | undefined;
  selectedShippingMethod: SelectedShippingMethodState;
  getUnitPriceToUse: (productId: string, variantId: string, originalUnitPrice: number) => number;
  /** Assignment-discount API effective_price only (no manual override, no coupon). */
  getEffectiveUnitPrice: (productId: string, variantId: string, originalUnitPrice: number) => number;
  /** Manual override → user-edited price; otherwise base/actual price (base_price from assignment API). */
  getUnitRetailPriceForPayload: (productId: string, variantId: string, originalUnitPrice: number) => number;
};

export async function buildSalesOrderPayloadForApi(
  values: any,
  deps: BuildSalesOrderPayloadDeps
): Promise<Record<string, unknown>> {
  const {
    token,
    ensureDraftUniqueId,
    shippingCosts,
    shipPayloadState,
    feePayloadState,
    selectedBillingAddress,
    selectedShippingAddress,
    productTotals,
    shippingTaxAmount,
    specialFeeTaxAmount,
    productTaxMap,
    perItemCouponMap,
    couponDiscounts,
    selectedBrokerageOption,
    selectedOfficeAgent,
    selectedAgent,
    selectedOffice,
    selectedTeamMemberOption,
    offices,
    selectedOfficeBillingAddress,
    selectedOfficeShippingAddress,
    addressObjects,
    projectPrefillAddresses,
    teamMemberFromContinueRef,
    customproducts,
    selectedProducts,
    ProductObjs,
    linkedProjectId,
    projectIdFromQuery,
    linkedProjectNumber,
    isSignageTeammember,
    parentSignageId,
    parentSignageName,
    userId,
    userName,
    selectedShippingMethod,
    getUnitPriceToUse,
    getUnitRetailPriceForPayload,
  } = deps;

    // ====================================================================
    // UNIQUE ID GENERATION - For draft orders
    // ====================================================================
    const cartId = localStorage.getItem("cart_id");
    const uniqueId = await ensureDraftUniqueId(token || "", cartId || null);

    // ====================================================================
    // HELPER FUNCTIONS - Local to onSubmit
    // ====================================================================
    const asNum = (v: any) => Number(v || 0);
    const round2 = (n: number) => Math.round(asNum(n) * 100) / 100;
    const keyForRow = (r: { productId: string; variantId: string }) => `${r.productId}:${r.variantId}`;
    const safeStr = (v: any) => (v === undefined || v === null ? "" : String(v));

    const mapUiShipToApi = (r: any) => {
      const id = safeStr(r.shipping__id ?? r.shipping_id ?? "");
      const cost = shippingCosts.find((c) => String(c._id) === id) as Record<string, unknown> | undefined;
      const fromRowPickup = r?.is_customer_pickup === true;
      const fromCostPickup = cost?.is_customer_pickup === true;
      const rowType = safeStr(r.shipping_type ?? "");
      const costType = safeStr(cost?.shipping_type ?? "");
      return {
        shipping_id: id,
        shipping_name: safeStr(r.shipping_name ?? ""),
        shipping_type: rowType || costType || (id === "custom-manual" ? "MANUAL" : ""),
        shipping_amount: asNum(r.shipping_amount),
        shipping_tax_percentage: asNum(r.shipping_tax_percentage),
        shipping_tax_amount: asNum(r.shipping_tax_amount),
        shipping_taxable: readResolveTaxable(
          r as Record<string, unknown>,
          ["shipping_taxable", "taxable", "is_taxable"],
          readResolveTaxable(cost, ["taxable", "is_taxable", "shipping_taxable"], true)
        ),
        shipping_delivery_date: safeStr(r.shipping_delivarydate ?? r.shipping_delivery_date ?? ""),
        shipping_delivery_time: safeStr(r.shipping_delivery_time ?? ""),
        is_customer_pickup: fromRowPickup || fromCostPickup,
      };
    };

    function buildItemTaxInfoAtCreate(
      row: {
        productId: string;
        variantId: string;
        sku: string;
        unitPrice: number;           // effective/changed unit price used for tax base
        changed_unitPrice: number;   // original unit (for audit)
        quantity: number;
        unit_discount_amount: number;
      },
      productTaxMap: Record<string, number>,
      shippingAddress: any,
      rowIsTaxable?: boolean
    ) {
      const k = keyForRow(row);
      const lineTax = asNum(productTaxMap[k] || 0);
      const qty = Math.max(1, asNum(row.quantity || 1));
      const unitTax = qty > 0 ? lineTax / qty : 0;
      const base = asNum(row.unitPrice || 0) * qty; // tax base = changed unit * qty
      const ratePct = base > 0 ? (lineTax / base) * 100 : 0;

      const appliedState = safeStr(
        shippingAddress?.address_details?.stateCode ||
        shippingAddress?.address_details?.state ||
        ""
      );

      const taxable = typeof rowIsTaxable === "boolean" ? rowIsTaxable : true;

      return {
        tax_percentage: round2(ratePct),
        unit_product_tax_amount: round2(unitTax),
        total_product_tax_amount: round2(lineTax),
        tax_details: {
          tax_rate: round2(ratePct),
          tax_amount: round2(lineTax),
          applied_state: appliedState,
        },
        taxable,
      };
    }

    // ---- pull latest shipping/special-fee rows from child payloads ----
    const shipPayload = (shipPayloadState ?? {}) as ShipPayload;
    const feePayload = (feePayloadState ?? {}) as FeePayload;
    const shippingRows = (shipPayload?.shipping_option ?? []) as any[];
    const specialRows = (feePayload?.specialfee ?? []) as any[];

    // destination for per-item tax info (prefer shipping; else billing)
    const shippingAddrForTax =
      (values.isSameAsBilling && selectedBillingAddress)
        ? selectedBillingAddress
        : (selectedShippingAddress || selectedBillingAddress || null);

    // ---- compute charges & tax (using your parent state) ----
    const shipSum = shippingRows.reduce((t, r) => t + asNum(r?.shipping_amount), 0);
    const feeSum = specialRows.reduce((t, r) => t + asNum(r?.specialfee_amount), 0);

    // product tax comes from TaxEngineUniversal; shipping/fee tax from their UIs
    const productTaxAmount = asNum(productTotals?.productTax ?? 0);
    const shippingTaxAmountNow = asNum(shippingTaxAmount ?? 0);
    const specialFeeTaxAmountNow = asNum(specialFeeTaxAmount ?? 0);
    const totalTax = productTaxAmount + shippingTaxAmountNow + specialFeeTaxAmountNow;

    // product subtotal (after discounts) already provided by the engine
    const subTotalProductsNow = asNum(productTotals?.subTotal ?? 0);

    // overall % (how much of base is tax)
    const overAllAmountWithoutTax = subTotalProductsNow + shipSum + feeSum;
    const overallPct =
      overAllAmountWithoutTax > 0 ? (totalTax / overAllAmountWithoutTax) * 100 : 0;


    // order tax status for create
    const taxStatusForCreate = "draft";

    // 👉 overall (order-level) discount = sum of per-item coupon allocations
    const couponTotal = round2(
      Object.values(perItemCouponMap || {}).reduce(
        (s: number, n: unknown) => s + Number(n || 0),
        0
      )
    );


    // final grand total = subtotal + charges + total tax
    const grandTotalNow = subTotalProductsNow + shipSum + feeSum + totalTax - couponTotal;
    // applied discount code(s)
    const appliedCodes = (couponDiscounts || [])
      .map(d => (d?.id || "").trim())
      .filter(Boolean);
    const discount_code = appliedCodes[0] || ""; // or join if multiple: appliedCodes.join(",")


    // ---- buyer context (agent/office) ----
    let brokerageFirmId: string | null = selectedBrokerageOption?.value ?? null;
    let officeInfo: any = null;
    let agentId: string | null = null;
    let agent_name: string | null = null;
    let customerId: string | null = null;
    let inventorySource: "agent" | "office" = "agent";
    let finalBillingAddress: any = null;
    let finalShippingAddress: any = null;

    if (selectedOfficeAgent) {
      agentId = selectedOfficeAgent.value;
      const first = selectedOfficeAgent.firstName ?? "";
      const last = selectedOfficeAgent.lastName ?? "";
      agent_name = (selectedOfficeAgent.label ?? `${first} ${last}`).trim();
      customerId = selectedOfficeAgent.value;
      inventorySource = "agent";
      brokerageFirmId = selectedBrokerageOption?.value ?? null;

      const selectedOfficeData = offices?.find((o) => o._id === selectedOffice?.value);
      const primaryAddress = selectedOfficeData?.officeAddress?.[0];
      officeInfo = {
        _id: selectedOffice?.value,
        officeName: selectedOffice?.label,
        officeType: selectedOffice?.label,
        officeAddress: primaryAddress,
        status: true,
      };
    } else if (selectedAgent) {
      agentId = selectedAgent.value;
      const first = selectedAgent.firstName ?? "";
      const last = selectedAgent.lastName ?? "";
      agent_name = (selectedAgent.label ?? `${first} ${last}`).trim();
      customerId = selectedAgent.value;
      inventorySource = "agent";
      officeInfo = null;
      // if this agent came with brokerage info from search, preserve it
      if ((selectedAgent as any)?.brokerageFirmId) {
        brokerageFirmId = (selectedAgent as any).brokerageFirmId;
      }
    } else if (selectedOffice) {
      const selectedOfficeData = offices?.find((o) => o._id === selectedOffice.value);
      const primaryAddress = selectedOfficeData?.officeAddress?.[0];

      officeInfo = {
        _id: selectedOffice.value,
        officeName: selectedOffice.label,
        officeType: selectedOffice.label,
        officeAddress: primaryAddress,
        status: true,
      };

      customerId = selectedBrokerageOption?.value ?? null;
      brokerageFirmId = selectedBrokerageOption?.value ?? brokerageFirmId;
      inventorySource = "office";
      agentId = null;
    }

    const billingFromList = findSalesOrderAddressById(addressObjects, values.billingAddressId);
    const shippingFromList = findSalesOrderAddressById(addressObjects, values.shippingAddressId);
    const billingRaw = firstAddressCandidate(
      selectedBillingAddress,
      selectedOfficeBillingAddress,
      billingFromList,
      projectPrefillAddresses?.billing
    );
    const shippingRaw = values.isSameAsBilling
      ? billingRaw
      : firstAddressCandidate(
          selectedShippingAddress,
          selectedOfficeShippingAddress,
          shippingFromList,
          projectPrefillAddresses?.shipping
        );
    finalBillingAddress = sanitizeSalesOrderAddressForPayload(billingRaw);
    finalShippingAddress = sanitizeSalesOrderAddressForPayload(shippingRaw);

    // End-user customer_id priority:
    // 1) Agent / Office Agent (optional agent selector overrides customer details)
    // 2) Team Member (if selected)
    // 3) Brokerage firm (fallback)

    const effectiveTeamMemberId = values.teamMemberId || selectedTeamMemberOption?.value || teamMemberFromContinueRef.current?.value;
    if (selectedOfficeAgent) {
      customerId = selectedOfficeAgent.value;
    } else if (selectedAgent) {
      customerId = selectedAgent.value;
    } else if (effectiveTeamMemberId) {
      customerId = effectiveTeamMemberId;
    } else if (!customerId && selectedBrokerageOption?.value) {
      customerId = selectedBrokerageOption.value;
    }

    // ====================================================================
    // PRODUCT MAPPING - Transform UI products to API format
    // ====================================================================
    // Custom products: no discount; tax only when is_taxable (from productTaxMap).
    // Build item_tax_info per line using keyForRow(productId, variantId).
    const custom_products = (customproducts || []).map((p) => {
      const customKey = keyForRow({ productId: p.product_id, variantId: p.variant_id });
      const isTaxable = readResolveTaxable(
        p as Record<string, unknown>,
        ["is_taxable", "taxable"],
        readResolveTaxable(p.item_tax_info as Record<string, unknown> | undefined, ["taxable"], true)
      );
      const lineTax = isTaxable ? asNum(productTaxMap[customKey] ?? 0) : 0;
      const qty = Math.max(1, asNum(p.quantity || 1));
      const unitPrice = asNum(p.Price || 0);
      const base = unitPrice * qty;
      const unitTax = qty > 0 ? lineTax / qty : 0;
      const ratePct = base > 0 ? (lineTax / base) * 100 : 0;
      const appliedState = safeStr(
        shippingAddrForTax?.address_details?.stateCode ||
        shippingAddrForTax?.address_details?.state ||
        ""
      );
      const item_tax_info = {
        tax_percentage: round2(ratePct),
        unit_product_tax_amount: round2(unitTax),
        total_product_tax_amount: round2(lineTax),
        tax_details: {
          tax_rate: round2(ratePct),
          tax_amount: round2(lineTax),
          applied_state: appliedState,
        },
        taxable: isTaxable,
      };
      return {
        product_id: p.product_id,
        variant_id: p.variant_id,
        warehouse_id: null,
        unit_purchase_price: p.Price,
        unit_retail_price: round(p.Price),  // Base retail price from product
        // unit_retail_price: round2(unitPriceShownInUI),  // Alternative: unit price shown in UI (manual or effective from API)
        changed_unit_price: round2(p.Price), // Final unit price after discount: (qty * Unit Price - discount) / qty

        // Discount fields
        discount_on_unit_quantity: 0.0,
        discount_on_total_quantity: 0.0,
        custom_attributes: {
          custom_information: p.custom_information
        },
        // Totals at line level
        total_price: round2(p.Price * (p.quantity || 1)),           // pre-discount line price
        item_sub_total: round2(p.Price * (p.quantity || 1)), // post-discount, pre-tax
        storage_location: null,
        is_production: true,
        images: [],
        customizations: {
        },
        product_name: p.item_name || "",  // User-entered name in payload
        quantity: p.quantity,
        sku: `custom_sku_${p.product_id}`, // Constructed SKU for custom products
        unit_price: p.Price,
        inventory_source: "custom",
        is_taxable: isTaxable,
        item_tax_info,
        custom_information: p.custom_information,
        ...(isTaxable && lineTax > 0 ? { item_tax_amount: round2(lineTax) } : {}),
        attributes: {
          length: p.length?.toString() || "",
          width: p.width?.toString() || "",
          height: p.height?.toString() || "",
          weight: p.weight?.toString() || "",
        }
      };
    });

    const products = (selectedProducts || []).map((p) => {
      const productObj = ProductObjs?.find((o) => o.product_id === p.productId);
      const variant = productObj?.product?.variants?.find((v: any) => v.variant_id === p.variantId);
      const locations = Array.isArray(variant?.inventory) ? variant.inventory : [];
      let storageLocation: any = null;

      if (locations.length === 1) {
        const w: any = locations[0];
        storageLocation = {
          warehouse_id: w.warehouse_id,
          warehouse: w.warehouse_name,
          zone_id: w.zone_id,
          zone: w.zone_name,
          rack_id: w.rack_id,
          rack: w.rack_name,
          bin_id: w.bin_id,
          bin: w.bin_name,
        };
      } else if (locations.length > 1) {
        const availableWarehouse =
          locations.find((w: any) => Number(w.available) > 0) || locations[locations.length - 1];
        const w: any = availableWarehouse;
        storageLocation = {
          warehouse_id: w.warehouse_id,
          warehouse: w.warehouse_name,
          zone_id: w.zone_id,
          zone: w.zone_name,
          rack_id: w.rack_id,
          rack: w.rack_name,
          bin_id: w.bin_id,
          bin: w.bin_name,
        };
      }

      // ================================================================
      // PRICE CALCULATIONS - Per product row
      // ================================================================
      const key = `${p.productId}:${p.variantId}`;
      const qty = asNum(p.quantity ?? 1);

      // Get unit price shown in UI (priority: manual > effective from API > original)
      const unitPriceShownInUI = getUnitPriceToUse(p.productId, p.variantId, asNum(p.unitPrice || 0));
      // resolve-assignment-discount effective_price (for changed_unit_price payload field)
      const purchase_price = asNum(productObj?.product?.purchase_price || 0);

      // Get coupon discount for this row
      const rowDiscTotal = asNum(perItemCouponMap?.[key] || 0);

      // Calculate line totals: base before discount, after discount, and changed unit price
      const lineBase = unitPriceShownInUI * qty;                    // base before discount
      const lineAfterDiscount = Math.max(0, lineBase - rowDiscTotal);
      const changedUnitPrice = round2(lineAfterDiscount / Math.max(1, qty));
      const perUnitDiscount = round2(rowDiscTotal / Math.max(1, qty)); // for unit-level field

      const isTaxable = resolveCatalogProductLineTaxable({
        catalogProduct: productObj?.product as Record<string, unknown> | undefined,
        row: p as Record<string, unknown>,
      });

      // tax info MUST use changed unit price as the taxable base
      const taxInfo = buildItemTaxInfoAtCreate(
        {
          productId: p.productId,
          variantId: p.variantId,
          sku: p.sku ?? variant?.sku ?? "",
          changed_unitPrice: unitPriceShownInUI,            // unit price shown in UI
          unitPrice: changedUnitPrice,            // effective/changed unit → tax base (after discount)
          quantity: qty,
          unit_discount_amount: perUnitDiscount,  // informational
        },
        productTaxMap || {},
        shippingAddrForTax,
        isTaxable
      );

      const lineTax = asNum(productTaxMap[key] ?? taxInfo.total_product_tax_amount ?? 0);

      // correct totals
      const item_sub_total = round2(lineAfterDiscount); // post-discount, pre-tax (uses UI/manual unit price)
      const items_total = round2(item_sub_total + lineTax);

      const hasStudioCustomizationPayload = Boolean(
        p.design_id && p.design_name && p.preview_url
      );
      const payloadImages = hasStudioCustomizationPayload
        ? [{ url: String(p.preview_url) }]
        : (Array.isArray(p.images) ? p.images : []);

      const productTypeOnly = String(
        p.product_type ?? productObj?.product?.product_type ?? "simple"
      ).trim() || "simple";

      return {
        product_id: p.productId,
        variant_id: p.variantId,
        warehouse_id: storageLocation?.warehouse_id || variant?.inventory?.[0]?.warehouse_id || null,
        product_name: variant?.variant_name || "",
        sku: variant?.sku || p.sku || "",

        quantity: qty,

        // ================================================================
        // PRICING FIELDS - Send to backend
        // ================================================================
        // Keep both for clarity in backend:
        unit_purchase_price: purchase_price,
        unit_retail_price: round2(
          getUnitRetailPriceForPayload(p.productId, p.variantId, asNum(p.unitPrice || 0))
        ),
        changed_unit_price: round2(changedUnitPrice), // UI unit price after per-item coupon discount

        // Discount fields
        discount_on_unit_quantity: perUnitDiscount,
        discount_on_total_quantity: round2(rowDiscTotal),
        inventory_source: "signage",
        // Totals at line level
        total_price: round2(items_total),           // pre-discount line price
        item_sub_total: round2(item_sub_total), // assignment effective_price × qty

        attributes: p.attributesList?.[0]?.attributes || {},
        storage_location: storageLocation,
        is_production: !!p.is_production,
        images: payloadImages,
        item_tax_info: taxInfo,
        customizations: {
          template_name: p.customizations?.template_name || "",
          fields: Array.isArray(p.customizations?.fields) ? p.customizations.fields : [],
        },
        ...(p.design_id
          ? {
              design_id: p.design_id,
              design_name: p.design_name || "",
              preview_url: p.preview_url || "",
            }
          : {}),
        product_type: productTypeOnly,
        is_taxable: isTaxable,
      };
    });

    // console.log("orderItems", {
    //       // identity / buyer info
    //       buyer_type: values.userType,
    //       signage_id: (isSignageTeammember && parentSignageId) ? parentSignageId : userId,
    //       signage_company_name: (isSignageTeammember && parentSignageName) ? parentSignageName : userName,

    //       office: officeInfo,
    //       brokerage_firm_name: selectedBrokerageOption?.label,
    //       brokerage_firm_id: brokerageFirmId,
    //       team_member_id: values.teamMemberId,
    //       team_member_name: values.teamMemberName,
    //       customer_id: customerId,
    //       agent_id: agentId,
    //       agent_name: agent_name,

    //       customer_first_name: values.userFirstName,
    //       customer_last_name: values.userLastName,
    //       customer_email: values.userEmail,
    //       customer_phone: values.userPhone,

    //       // discount code applied at edit time
    //       discount_code,
    //       // addresses
    //       cart_id: null,
    //       shipping_address: finalShippingAddress,
    //       billing_address: finalBillingAddress,
    //       both_address_same: !!values.isSameAsBilling,

    //       // items
    //       items: products,
    //       order_type: "sales",
    //       inventory_source: inventorySource,

    //       // misc
    //       customer_group_id: "",
    //       payment_method: "",
    //       payment_status: "Pending",
    //       payment_info: [],
    //       tax_inclusive: false,

    //       // simple tax header (compat)
    //       tax_details: {
    //         tax_rate: 0,
    //         tax_amount: 0,
    //         applied_state: "",
    //       },

    //       // totals (accurate)
    //       subtotal_amount: round2(subTotalProductsNow),
    //       discount_amount: round2(couponTotal),     // order-level = sum of per-item allocations
    //       tax_amount: round2(totalTax),
    //       total_amount: round2(grandTotalNow),

    //       // shipping method fields
    //       manual_shipping_method: {
    //         _id: selectedShippingMethod?.value,
    //         name: selectedShippingMethod?.label,
    //         cost: selectedShippingMethod?.cost,
    //       },
    //       shipping_method: {
    //         carrier_name: "",
    //         service_name: "",
    //         tracking_number: "",
    //         cost: 0,
    //       },

    //       notes: safeStr(values.notes ?? "").trim(),
    //       status_history: [],

    //       // aggregated tax summary
    //       order_tax_summary: {
    //         tax_inclusive: false,
    //         overall_tax_amount: round2(totalTax),
    //         overall_tax_percentage: Number(overallPct.toFixed(4)),
    //         overall_taxable_base: round2(overAllAmountWithoutTax),
    //         tax_status: taxStatusForCreate,
    //       },

    //       // charges as arrays (shipping + specialfee)
    //       order_charges: [
    //         {
    //           shipping_option: shippingRows.map(mapUiShipToApi),
    //           specialfee: (specialRows || []).map((f: any) => ({
    //             specialfee_id: safeStr(f.specialfee_id ?? ""),
    //             specialfee_name: safeStr(f.specialfee_name ?? ""),
    //             specialfee_amount: asNum(f.specialfee_amount),
    //             specialfee_tax_percentage: asNum(f.specialfee_tax_percentage),
    //             specialfee_tax_amount: asNum(f.specialfee_tax_amount),
    //             specialfee_taxable: f?.specialfee_taxable !== false,
    //           })),
    //         },
    //       ],
    //     }
    // );

    // ====================================================================
    // ORDER PAYLOAD CONSTRUCTION - Final API request body
    // ====================================================================
    const orderPayload = {
      // ============================================================
      // BUYER & ORGANIZATION INFO
      // ============================================================
      buyer_type: values.userType,
      signage_id: (isSignageTeammember && parentSignageId) ? parentSignageId : userId,
      signage_company_name: (isSignageTeammember && parentSignageName) ? parentSignageName : userName,

      office: officeInfo,
      brokerage_firm_name:
        (selectedBrokerageOption as any)?.label ||
        ((selectedAgent as any)?.brokerageFirmName ?? null),
      brokerage_firm_id: brokerageFirmId,
      team_member_id: (values.teamMemberId || (selectedTeamMemberOption as any)?.value || teamMemberFromContinueRef.current?.value) ?? "",
      team_member_name: (values.teamMemberName || (selectedTeamMemberOption as any)?.label || teamMemberFromContinueRef.current?.label) ?? "",
      customer_id: customerId,
      agent_id: agentId,
      agent_name: agent_name,

      customer_first_name: values.userFirstName,
      customer_last_name: values.userLastName,
      customer_email: values.userEmail,
      customer_phone: values.userPhone,

      // ============================================================
      // DISCOUNTS & ADDRESSES
      // ============================================================
      discount_code,  // Applied coupon code
      cart_id: uniqueId || null,
      unique_id: uniqueId || null,
      shipping_address: finalShippingAddress,
      billing_address: finalBillingAddress,
      both_address_same: !!values.isSameAsBilling,

      // ============================================================
      // ORDER ITEMS & TYPE
      // ============================================================
      // items: single array [ catalog product, catalog product, custom product, custom product, ... ]
      items: [...products, ...custom_products],
      order_type: "sales",
      project_id: linkedProjectId || projectIdFromQuery || undefined,
      project_number: linkedProjectNumber || undefined,
      inventory_source: inventorySource,

      // misc
      customer_group_id: "",
      payment_method: "",
      payment_status: "Pending",
      payment_info: [],
      tax_inclusive: false,

      // simple tax header (compat)
      tax_details: {
        tax_rate: 0,
        tax_amount: 0,
        applied_state: "",
      },

      // ============================================================
      // FINANCIAL TOTALS
      // ============================================================
      subtotal_amount: round2(subTotalProductsNow),
      discount_amount: round2(couponTotal),     // Order-level = sum of per-item allocations
      shipping_amount: round2(shipSum),
      tax_amount: round2(totalTax),
      total_amount: round2(grandTotalNow),

      // shipping method fields
      // manual_shipping_method: {
      //   _id: selectedShippingMethod?.value,
      //   name: selectedShippingMethod?.label,
      //   cost: selectedShippingMethod?.cost,
      // },
      shipping_method: {
        carrier_name: "",
        service_name: "",
        tracking_number: "",
        cost: round2(shipSum),
      },

      notes: safeStr(values.notes ?? "").trim(),
      po_number: safeStr(values.po_number ?? "").trim(),
      status_history: [],

      // ============================================================
      // TAX SUMMARY
      // ============================================================
      order_tax_summary: {
        tax_inclusive: false,
        over_all_tax_amount: round2(totalTax),
        over_all_tax_percentage: Number(overallPct.toFixed(4)),
        over_all_taxable_base: round2(overAllAmountWithoutTax),
        tax_status: taxStatusForCreate,
      },

      // ============================================================
      // ORDER CHARGES - Shipping & Special Fees
      // ============================================================
      order_charges: [
        {
          shipping_option: shippingRows.map(mapUiShipToApi),
          specialfee: (specialRows || []).map((f: any) => ({
            specialfee_id: safeStr(f.specialfee_id ?? ""),
            specialfee_name: safeStr(f.specialfee_name ?? ""),
            specialfee_amount: asNum(f.specialfee_amount),
            specialfee_tax_percentage: asNum(f.specialfee_tax_percentage),
            specialfee_tax_amount: asNum(f.specialfee_tax_amount),
            specialfee_taxable: readResolveTaxable(
              f as Record<string, unknown>,
              ["specialfee_taxable", "taxable", "is_taxable"],
              true
            ),
            required: readResolveRequired(f as Record<string, unknown>, false),
          })),
        },
      ],
    };


    return orderPayload;
}
